/**
 * Permission helpers for session status/role checks and policy evaluation.
 */

import { STORAGE_KEYS } from "/assets/js/config.js";
import { ACTION_POLICIES, PAGE_POLICIES } from "/assets/js/permission_control/policy-map.js";

/** @type {Set<string> | null} */
let effectiveActions = null;

/**
 * @param {string} [rawStatus]
 * @returns {string}
 */
export function normalizeStatus(rawStatus) {
  const normalized = String(rawStatus || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "";
  if (normalized === "active") return "ativo";
  if (normalized === "inactive") return "inativo";
  return normalized;
}

export function getCurrentPermission() {
  return localStorage.getItem(STORAGE_KEYS.USER_PERMISSION);
}

export function getCurrentStatus() {
  return normalizeStatus(localStorage.getItem(STORAGE_KEYS.USER_STATUS));
}

export function isUserActive() {
  const status = getCurrentStatus();
  if (!status) return true;
  return status === "ativo";
}

/**
 * @param {string[]} allowedRoles
 * @returns {boolean}
 */
export function hasAnyRole(allowedRoles) {
  if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) return true;
  const currentPermission = getCurrentPermission();
  if (!currentPermission) return false;
  return allowedRoles.includes(currentPermission);
}

/**
 * @param {string} pageName
 * @returns {boolean}
 */
export function canAccessPage(pageName) {
  const allowedRoles =
    PAGE_POLICIES[
      String(pageName || "")
        .trim()
        .toLowerCase()
    ];
  if (!allowedRoles) return true;
  return hasAnyRole(allowedRoles);
}

/**
 * Store session-effective action keys from GET /configuration/access-policy/effective.
 * `null` clears the set so factory `ACTION_POLICIES` is used.
 * @param {Iterable<unknown> | string | null | undefined} actions
 */
export function setEffectiveActions(actions) {
  if (actions == null) {
    effectiveActions = null;
    return;
  }
  const list = typeof actions === "string" ? [actions] : Array.from(actions);
  effectiveActions = new Set(
    list.map((item) => String(item ?? "").trim()).filter(Boolean),
  );
}

/** Drop the loaded set so `canPerformAction` uses factory `ACTION_POLICIES`. */
export function clearEffectiveActions() {
  effectiveActions = null;
}

/**
 * @param {string} actionKey
 * @returns {boolean}
 */
export function canPerformAction(actionKey) {
  const key = String(actionKey || "").trim();
  if (effectiveActions) {
    return effectiveActions.has(key);
  }
  const allowedRoles = ACTION_POLICIES[key];
  if (!allowedRoles) return false;
  return hasAnyRole(allowedRoles);
}

export function clearPermissionSession() {
  clearEffectiveActions();
  localStorage.removeItem(STORAGE_KEYS.USER_PERMISSION);
  localStorage.removeItem(STORAGE_KEYS.USER_STATUS);
}

export const PermissionControl = {
  getCurrentPermission,
  getCurrentStatus,
  normalizeStatus,
  isUserActive,
  hasAnyRole,
  canAccessPage,
  canPerformAction,
  setEffectiveActions,
  clearEffectiveActions,
  clearPermissionSession,
};
